import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PencilSquare, Trash, X, JournalText, CheckLg, XLg } from "react-bootstrap-icons";
import { createApiService } from "../shared/apiConfig";
import { ParkerNoteApi, ServerRequestsParkerNoteDto } from "../shared/api/axios-client";

const api = createApiService(ParkerNoteApi);
import { ConfirmModal } from "./ConfirmModal";
import { getInitials, avatarColor } from "../Data/Avatar";
import "./ParkerNotesDrawer.css";

interface Props {
    facilityId:     number;
    parkerId:       number;
    parkerName:     string;
    currentStaffId: number | undefined;
    canAddNotes:    boolean;
    isOpen:         boolean;
    onClose:        () => void;
    onCountChange?: (count: number) => void;
}

type Tab = "all" | "mine";

function formatNoteDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
        month: "short",
        day:   "numeric",
        year:  "numeric",
        hour:  "numeric",
        minute: "2-digit",
    });
}

function highlightText(text: string, search: string): React.ReactNode {
    if (!search) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part
    );
}

export function ParkerNotesDrawer({ facilityId, parkerId, parkerName, currentStaffId, canAddNotes, isOpen, onClose, onCountChange }: Props) {
    const [notes,      setNotes]      = useState<ServerRequestsParkerNoteDto[]>([]);
    const [tab,        setTab]        = useState<Tab>("all");
    const [search,     setSearch]     = useState("");
    const [newText,    setNewText]    = useState("");
    const [editingId,  setEditingId]  = useState<number | undefined>(undefined);
    const [editText,      setEditText]      = useState("");
    const [editOriginal,  setEditOriginal]  = useState("");
    const [saving,        setSaving]        = useState(false);
    const [visible,       setVisible]       = useState(false);
    const [entered,       setEntered]       = useState(false);
    const [confirmNote,   setConfirmNote]   = useState<ServerRequestsParkerNoteDto | null>(null);
    const panelRef   = useRef<HTMLDivElement>(null);
    const newNoteRef = useRef<HTMLTextAreaElement>(null);

    const focusNewNote = () => newNoteRef.current?.focus();

    // Drive open/close CSS transition
    // `visible` controls mount; `entered` controls the .open CSS class.
    // On close we remove .open first, then unmount after the 300ms transition finishes.
    // Body scroll is locked while the drawer is open to prevent double-scrollbar.
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            setVisible(true);
            // Double-rAF: first frame mounts the element, second triggers the CSS transition
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setEntered(true));
            });
            loadNotes();
        } else {
            setEntered(false);
            const t = setTimeout(() => {
                document.body.style.overflow = "";
                setVisible(false);
                setSearch("");
                setEditingId(undefined);
            }, 310);
            return () => clearTimeout(t);
        }
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    const loadNotes = async () => {
        try {
            const res = await api.listParkerNotes({ facilityId, parkerId });
            const data = res.data.notes ?? [];
            setNotes(data);
            onCountChange?.(data.length);
        } catch {
            // silently fail; notes unavailable
        }
    };

    // Filtered view
    const filtered = notes
        .filter(n => tab === "mine" ? n.staffId === currentStaffId : true)
        .filter(n => !search || (n.noteText ?? "").toLowerCase().includes(search.toLowerCase()));

    const handleAdd = async () => {
        if (!newText.trim()) return;
        setSaving(true);
        try {
            const res = await api.createParkerNote({
                facilityId,
                parkerId,
                serverRequestsCreateParkerNoteRequest: { noteText: newText.trim() },
            });
            if (res.data.note) {
                setNotes(prev => [res.data.note!, ...prev]);
                onCountChange?.(notes.length + 1);
            }
            setNewText("");
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (note: ServerRequestsParkerNoteDto) => {
        const text = note.noteText ?? "";
        setEditingId(note.parkerNoteId);
        setEditText(text);
        setEditOriginal(text);
    };

    const cancelEdit = () => {
        setEditingId(undefined);
        setEditText("");
    };

    const handleSaveEdit = async (note: ServerRequestsParkerNoteDto) => {
        if (!editText.trim()) return;
        setSaving(true);
        try {
            const res = await api.updateParkerNote({
                facilityId,
                parkerId,
                noteId: note.parkerNoteId!,
                serverRequestsUpdateParkerNoteRequest: { noteText: editText.trim() },
            });
            if (res.data.note) {
                setNotes(prev => prev.map(n => n.parkerNoteId === note.parkerNoteId ? res.data.note! : n));
            }
            setEditingId(undefined);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (note: ServerRequestsParkerNoteDto) => {
        setConfirmNote(note);
    };

    const confirmDelete = async () => {
        if (!confirmNote) return;
        try {
            await api.deleteParkerNote({ facilityId, parkerId, noteId: confirmNote.parkerNoteId! });
            const next = notes.filter(n => n.parkerNoteId !== confirmNote.parkerNoteId);
            setNotes(next);
            onCountChange?.(next.length);
        } catch {
            // error handled by axios interceptor
        } finally {
            setConfirmNote(null);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (!panelRef.current?.contains(e.target as Node)) {
            onClose();
        }
    };

    if (!visible) return null;

    return createPortal(
        <>
            <div
                className={`slide-over-overlay notes-overlay ${entered ? "open" : ""}`}
                onClick={handleOverlayClick}
            />

            <div
                ref={panelRef}
                className={`slide-over notes-drawer ${entered ? "open" : ""}`}
                role="dialog"
                aria-label="Parker notes"
            >
                {/* Header */}
                <div className="slide-over-header" style={{ justifyContent: "space-between" }}>
                    <span className="d-flex align-items-center gap-2">
                        <JournalText size={16} />
                        <span className="menu-title">Notes</span>
                    </span>
                    <button
                        onClick={onClose}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: "0.25rem", color: "#555" }}
                        aria-label="Close notes"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="notes-drawer-tabs">
                    <button
                        className={`notes-drawer-tab${tab === "all" ? " active" : ""}`}
                        onClick={() => setTab("all")}
                    >
                        All
                    </button>
                    <button
                        className={`notes-drawer-tab${tab === "mine" ? " active" : ""}`}
                        onClick={() => setTab("mine")}
                    >
                        Mine
                    </button>
                </div>

                {/* Search */}
                <div className="notes-drawer-search">
                    <input
                        type="text"
                        placeholder="Search notes..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                {/* Notes list */}
                <div className="notes-list">
                    {filtered.length === 0 ? (
                        <div className="notes-empty">
                            {search ? (
                                "No notes match your search."
                            ) : notes.length === 0 ? (
                                <>
                                    <div className="notes-empty-icon">
                                        <JournalText size={24} />
                                    </div>
                                    <p className="notes-empty-title">
                                        No notes have been added for {parkerName} yet
                                    </p>
                                    <p className="notes-empty-subtitle">
                                        Add a note to keep track of important details for this account.
                                    </p>
                                    {canAddNotes && (
                                    <button type="button" className="notes-empty-cta" onClick={focusNewNote}>
                                        Add a note
                                    </button>
                                    )}
                                </>
                            ) : (
                                "You haven't added any notes yet."
                            )}
                        </div>
                    ) : (
                        filtered.map(note => {
                            const isOwn     = note.staffId === currentStaffId;
                            const isEditing = editingId !== undefined && editingId === note.parkerNoteId;

                            return (
                                <div key={note.parkerNoteId} className="note-card">

                                    {isEditing ? (
                                        <div className="note-card-edit-area">
                                            <textarea
                                                value={editText}
                                                onChange={e => setEditText(e.target.value)}
                                                maxLength={1000}
                                                autoFocus
                                            />
                                            <div className="note-card-edit-actions">
                                                <button
                                                    className="note-card-action-btn delete"
                                                    onClick={cancelEdit}
                                                    title="Cancel"
                                                >
                                                    <XLg size={11} /> Cancel
                                                </button>
                                                <button
                                                    className="note-card-action-btn"
                                                    onClick={() => handleSaveEdit(note)}
                                                    disabled={saving || !editText.trim() || editText.trim() === editOriginal.trim()}
                                                    title="Save"
                                                    style={{ background: saving ? undefined : undefined }}
                                                >
                                                    <CheckLg size={11} /> Save
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="note-card-body">
                                            {highlightText(note.noteText ?? "", search)}
                                        </div>
                                    )}

                                    <div className="note-card-footer">
                                        <div className="note-card-footer-meta">
                                            <span
                                                className="note-card-avatar"
                                                style={{ background: avatarColor(note.staffName ?? "") }}
                                                title={note.staffName ?? ""}
                                            >
                                                {getInitials(note.staffName ?? "")}
                                            </span>
                                            <span className="note-card-author">{note.staffName}</span>
                                            <span className="note-card-date-sep">·</span>
                                            <span className="note-card-date">{formatNoteDate(note.createdWhen as unknown as string)}</span>
                                            {note.isEdited && (
                                                <span className="note-card-edited-badge">edited</span>
                                            )}
                                        </div>
                                        {isOwn && canAddNotes && !isEditing && (
                                            <div className="note-card-actions">
                                                <button
                                                    className="note-card-action-btn"
                                                    onClick={() => startEdit(note)}
                                                    title="Edit note"
                                                >
                                                    <PencilSquare size={11} />
                                                </button>
                                                <button
                                                    className="note-card-action-btn delete"
                                                    onClick={() => handleDelete(note)}
                                                    title="Delete note"
                                                >
                                                    <Trash size={11} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Add note */}
                {canAddNotes && (
                <div className="notes-add-area">
                    <textarea
                        ref={newNoteRef}
                        placeholder={`Add a note about ${parkerName}...`}
                        value={newText}
                        maxLength={1000}
                        onChange={e => setNewText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAdd();
                        }}
                    />
                    <button
                        className="notes-add-btn"
                        onClick={handleAdd}
                        disabled={saving || !newText.trim() || newText.length > 1000}
                    >
                        Add Note
                    </button>
                </div>
                )}
            </div>

            <ConfirmModal
                show={!!confirmNote}
                setShow={show => { if (!show) setConfirmNote(null); }}
                title="Delete Note"
                message="Are you sure you want to delete this note?"
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
            />
        </>,
        document.body
    );
}
